import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { QuestionItem } from 'screens/settings/DueDiligenceCategoryEditor/types';
import { createEmptyQuestion } from 'screens/settings/DueDiligenceCategoryEditor/types';
import { QUESTION_TEMPLATES } from 'components/dueDiligence/templates';
import { WizardStepIndicator } from './WizardStepIndicator';
import { WizardStep1TemplateSelect } from './WizardStep1TemplateSelect';
import { WizardStep2Configure } from './WizardStep2Configure';
import { WizardStep3AnswerOptions } from './WizardStep3AnswerOptions';
import { WizardStep4Review } from './WizardStep4Review';

interface QuestionWizardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (question: QuestionItem) => void;
  existingQuestions?: QuestionItem[];
}

const STEP_LABELS = ['Sjabloon', 'Configuratie', 'Antwoorden', 'Overzicht'];
const TOTAL_STEPS = 4;

export function QuestionWizardDialog({
  open,
  onOpenChange,
  onComplete,
  existingQuestions,
}: QuestionWizardDialogProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [question, setQuestion] = useState<QuestionItem>(createEmptyQuestion());
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset state when closing
      setCurrentStep(1);
      setQuestion(createEmptyQuestion());
      setSelectedTemplateId(null);
    }
    onOpenChange(newOpen);
  };

  const handleTemplateSelect = (templateId: string | null) => {
    try {
      setSelectedTemplateId(templateId);

      if (templateId === null) {
        setQuestion(createEmptyQuestion());
      } else {
        const template = QUESTION_TEMPLATES.find((t) => t.id === templateId);
        if (template) {
          const newQuestion = template.createQuestion();
          setQuestion(newQuestion);
        }
      }
    } catch (error) {
      console.error('Error selecting template:', error);
      throw error;
    }
  };

  const handleQuestionUpdate = (updates: Partial<QuestionItem>) => {
    try {
      setQuestion((prev) => ({ ...prev, ...updates }));
    } catch (error) {
      console.error('Error updating question:', error);
      throw error;
    }
  };

  const canGoNext = () => {
    switch (currentStep) {
      case 1:
        return true; // Can always proceed from template selection
      case 2:
        return question.displayName.trim().length > 0; // Must have a display name
      case 3:
        return true; // Can always proceed from answer options
      case 4:
        return false; // Last step, no next
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (canGoNext() && currentStep < TOTAL_STEPS) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSave = () => {
    try {
      onComplete(question);
      handleOpenChange(false);
    } catch (error) {
      console.error('Error saving question:', error);
      throw error;
    }
  };

  const handleExpertMode = () => {
    try {
      onComplete(createEmptyQuestion());
      handleOpenChange(false);
    } catch (error) {
      console.error('Error entering expert mode:', error);
      throw error;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <WizardStep1TemplateSelect
            selectedTemplateId={selectedTemplateId}
            onSelectTemplate={handleTemplateSelect}
          />
        );
      case 2:
        return <WizardStep2Configure question={question} onUpdate={handleQuestionUpdate} />;
      case 3:
        return (
          <WizardStep3AnswerOptions
            question={question}
            onUpdate={handleQuestionUpdate}
            existingQuestions={existingQuestions}
          />
        );
      case 4:
        return <WizardStep4Review question={question} />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Nieuwe vraag toevoegen</DialogTitle>
        </DialogHeader>

        <WizardStepIndicator currentStep={currentStep} totalSteps={TOTAL_STEPS} stepLabels={STEP_LABELS} />

        <div className="flex-1 overflow-y-auto px-1">
          {renderStepContent()}
        </div>

        <DialogFooter className="flex items-center justify-between border-t pt-4">
          <button
            type="button"
            onClick={handleExpertMode}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Expert modus
          </button>

          <div className="flex gap-2">
            {currentStep > 1 && (
              <Button type="button" variant="outline" onClick={handlePrevious}>
                Vorige
              </Button>
            )}

            {currentStep < TOTAL_STEPS ? (
              <Button type="button" onClick={handleNext} disabled={!canGoNext()}>
                Volgende
              </Button>
            ) : (
              <Button type="button" onClick={handleSave}>
                Opslaan
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
